// src/context/CartContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { stockData } from '../data/stock';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  oneOfKind?: boolean; // Add oneOfKind flag
  madeToOrder?: boolean; // Made to order items (0 stock but purchasable)
  slug: string; // Add slug to interface
  variantId?: string | null; // Add variantId to interface
}

interface CartContextType {
  items: CartItem[];
  email: string | null;
  setEmail: (email: string) => void;
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  const addItem = (item: CartItem) => {
    const fullImageUrl = item.image.startsWith('http')
      ? item.image
      : `${window.location.origin}${item.image}`;

    setItems(currentItems => {
      const existingItem = currentItems.find(i => i.id === item.id);
      const currentStock = stockData[item.id] ?? 0;

      // Made-to-order items don't have stock limits
      const isMadeToOrder = item.madeToOrder === true;

      if (existingItem) {
        // Don't increase quantity for one of kind items
        if (item.oneOfKind) {
          return currentItems;
        }
        // For made-to-order items, allow unlimited quantity increases
        if (isMadeToOrder) {
          return currentItems.map(i =>
            i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
          );
        }
        // For in-stock items, respect stock limit
        if (existingItem.quantity >= currentStock) {
          return currentItems;
        }
        return currentItems.map(i =>
          i.id === item.id ? { ...i, quantity: Math.min(i.quantity + 1, currentStock) } : i
        );
      }
      // New item: always add with quantity 1 (product page handles out-of-stock blocking)
      return [...currentItems, { ...item, image: fullImageUrl, quantity: 1 }];
    });
  };

  const removeItem = (id: string) => {
    setItems(currentItems => currentItems.filter(item => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    // If quantity is 0 or less, remove the item
    if (quantity <= 0) {
      removeItem(id);
      return;
    }

    setItems(currentItems =>
      currentItems.map(item => {
        // Don't update quantity for one of kind items
        if (item.id === id && item.oneOfKind) {
          return item;
        }
        return item.id === id ? { ...item, quantity } : item;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  return (
    <CartContext.Provider value={{ 
      items, 
      email,
      setEmail,
      addItem, 
      removeItem, 
      updateQuantity, 
      clearCart 
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};